/**
 * Notification settings card for how much detail message notifications reveal
 * (sender, conversation, channel, Space), separately for conversations and
 * Spaces. Applies to toasts, system notifications, and the inbox alike, and is
 * stored server-side per alias.
 */

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONVERSATION_NOTIFICATION_DISPLAY_FIELDS,
  SPACE_NOTIFICATION_DISPLAY_FIELDS,
  type NotificationDisplaySettings,
} from '@adieuu/shared';
import { Card } from '../../components/Card';
import { useNotificationDisplaySettings } from '../../hooks/useNotificationDisplaySettings';
import {
  describeConversationNotification,
  describeSpaceNotification,
  type NotificationCopy,
  type NotificationTranslate,
} from '../../utils/notificationCopy';
import '../../styles/_notification-details-card.scss';

const PREFIX = 'account.settings.notifications.details';

type Section = keyof NotificationDisplaySettings;

function Preview({ copy, label }: { copy: NotificationCopy; label: string }) {
  return (
    <div className="notification-details-preview" aria-live="polite">
      <span className="notification-details-preview-label">{label}</span>
      <span className="notification-details-preview-title">{copy.title}</span>
      <span className="notification-details-preview-body">{copy.body}</span>
    </div>
  );
}

export const NotificationDetailsCard = forwardRef<HTMLDivElement>(function NotificationDetailsCard(_props, ref) {
  const { t } = useTranslation();
  const { available, display, setDisplay } = useNotificationDisplaySettings();
  if (!available) return null;

  const translate = t as unknown as NotificationTranslate;
  const senderName = t(`${PREFIX}.previewSender`);
  const conversationPreview = describeConversationNotification({
    kind: 'message',
    senderName,
    conversation: { type: 'group', name: t(`${PREFIX}.previewConversationName`) },
    display: display.conversations,
    t: translate,
  });
  const spacePreview = describeSpaceNotification({
    kind: 'reply',
    senderName,
    channelName: t(`${PREFIX}.previewChannelName`),
    spaceName: t(`${PREFIX}.previewSpaceName`),
    display: display.spaces,
    t: translate,
  });

  const toggle = <S extends Section>(section: S, field: keyof NotificationDisplaySettings[S], value: boolean) => {
    void setDisplay({ ...display, [section]: { ...display[section], [field]: value } });
  };

  const renderToggles = <S extends Section>(section: S, fields: readonly (keyof NotificationDisplaySettings[S] & string)[]) =>
    fields.map((field) => (
      <label key={field} className="app-settings-toggle">
        <input
          type="checkbox"
          checked={display[section][field] as boolean}
          onChange={(e) => toggle(section, field, e.target.checked)}
        />
        <span className="app-settings-toggle-label">
          <span className="app-settings-toggle-title">{t(`${PREFIX}.${field}Toggle`)}</span>
          <span className="app-settings-toggle-hint">{t(`${PREFIX}.${field}Hint`)}</span>
        </span>
      </label>
    ));

  return (
    <Card variant="elevated" className="slide-up app-settings-card" ref={ref} data-section="notification-details">
      <h2 className="app-settings-section-title">{t(`${PREFIX}.sectionTitle`)}</h2>
      <p className="app-settings-section-desc">{t(`${PREFIX}.sectionDescription`)}</p>

      <fieldset className="notification-details-group">
        <legend className="app-settings-toggle-title">{t(`${PREFIX}.conversationsTitle`)}</legend>
        {renderToggles('conversations', CONVERSATION_NOTIFICATION_DISPLAY_FIELDS)}
        <Preview copy={conversationPreview} label={t(`${PREFIX}.previewLabel`)} />
      </fieldset>

      <fieldset className="notification-details-group">
        <legend className="app-settings-toggle-title">{t(`${PREFIX}.spacesTitle`)}</legend>
        {renderToggles('spaces', SPACE_NOTIFICATION_DISPLAY_FIELDS)}
        <Preview copy={spacePreview} label={t(`${PREFIX}.previewLabel`)} />
      </fieldset>
    </Card>
  );
});
